import { WitzoChatElement } from './chat/chat-widget';
import { templateWidget } from './template';
import { BaseUIElements, Flags, Nullable } from './types';
import { WitzoVoiceElement } from './voice/voice-widget';
import { recoverOrphanedSession } from './voice/speech';

function ensureDefined(name: string, ctor: CustomElementConstructor) {
  if (!customElements.get(name)) {
    customElements.define(name, ctor);
  }
}

function rememberWidgetClosed() {
  try {
    localStorage.setItem('witzo_widget_closed', 'true');
  } catch {
    /* localStorage can be unavailable in Safari privacy/embed contexts. */
  }
}

const widget = document.createElement('template');
widget.innerHTML = templateWidget;

class WitzoVoiceChat extends HTMLElement {
  root: ShadowRoot;
  /**Url to connect to the webhook */
  private _url: Nullable<string> = null;
  private _agentId: Nullable<string> = null;
  private _defaultText: Nullable<string> = null;
  private _botBackgroundColor: Nullable<string> = null;
  private _sendBackgroundColor: Nullable<string> = null;
  private _floatingBtnColor: Nullable<string> = null;
  private _chatIconColor: Nullable<string> = null;
  private _voiceIconColor: Nullable<string> = null;

  /**Reference for elements */
  chat: Nullable<BaseUIElements> = null;
  flags: Flags;
  private autoOpenTimer: ReturnType<typeof window.setTimeout> | null = null;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(node);

    this.flags = {
      isInitialized: false,
      isOpen: false,
      isButtonClicked: false,
    };
    this.root = root;
    this.getElements(root);
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selector: string) => root.querySelector(selector) as T;

    try {
      this.chat = {
        chatSelectBtn: query('#selectTextChat'),
        chatWindow: query('#chatWidget'),
        floatingButton: query('#floatingBtn'),
        selectionWindow: query('#selectionPopup'),
        voiceSelectBtn: query('#selectVoiceChat'),
        floatingBtn: query('#floating-btn'),
        chatIcon: root.querySelector('#chat-icon-color') as SVGPathElement,
        voiceIcon: root.querySelector('#voice-icon-color') as SVGPathElement,
      };
      this.flags.isInitialized = true;
    } catch (err) {
      this.flags.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatInfo(info: {
    url: string;
    apiBaseUrl?: Nullable<string>;
    defaultText: Nullable<string>;
    botColor: Nullable<string>;
    sendColor: Nullable<string>;
    floatingBtn: Nullable<string>;
    iconColor: Nullable<string>;
    uniqueId: Nullable<string>;
    bannerText: Nullable<string>;
    bannerTextColor: Nullable<string>;
    chatHeader: Nullable<string>;
    logoIcon: Nullable<string>;
    closeButtonColor: Nullable<string>;
    userChatColor: Nullable<string>;
    bannerTextParagraph: Nullable<string>;
    bannerTextParagraphColor: Nullable<string>;
    defaultLanguage?: Nullable<string>;
  }) {
    this._url = info.url;
    this._defaultText = info.defaultText ?? null;
    this._botBackgroundColor = info.botColor ?? null;
    this._sendBackgroundColor = info.sendColor ?? null;
    this.defineChatAgent(info);
  }

  private defineChatAgent(info: {
    url: string;
    apiBaseUrl?: Nullable<string>;
    defaultText: Nullable<string>;
    botColor: Nullable<string>;
    sendColor: Nullable<string>;
    floatingBtn: Nullable<string>;
    iconColor: Nullable<string>;
    uniqueId: Nullable<string>;
    bannerText: Nullable<string>;
    bannerTextColor: Nullable<string>;
    chatHeader: Nullable<string>;
    logoIcon: Nullable<string>;
    closeButtonColor: Nullable<string>;
    userChatColor: Nullable<string>;
    bannerTextParagraph: Nullable<string>;
    bannerTextParagraphColor: Nullable<string>;
    defaultLanguage?: Nullable<string>;
  }) {
    ensureDefined('witzo-chat-element', WitzoChatElement);
    const chatElement = this.root.querySelector('witzo-chat-element') as WitzoChatElement;
    chatElement.chatUrl = info.url;
    chatElement.apiBaseURL = info.apiBaseUrl ?? '';
    chatElement.botBackgroundColor = info.botColor ?? '#fc0e3f';
    chatElement.sendBackgroundColor = info.sendColor ?? '#fc0e3f';
    chatElement.uniqueId = info.uniqueId ?? '';

    // Customization props
    if (info.bannerText) chatElement.bannerText = info.bannerText;
    if (info.bannerTextColor) chatElement.bannerTextColor = info.bannerTextColor;
    if (info.chatHeader) chatElement.chatHeader = info.chatHeader;
    if (info.logoIcon) chatElement.logoIcon = info.logoIcon;
    if (info.closeButtonColor) chatElement.closeButtonColor = info.closeButtonColor;
    if (info.userChatColor) chatElement.userChatColor = info.userChatColor;
    if (info.bannerTextParagraph) chatElement.bannerTextParagraph = info.bannerTextParagraph;
    if (info.bannerTextParagraphColor) chatElement.bannerTextParagraphColor = info.bannerTextParagraphColor;
    if (info.defaultLanguage) chatElement.defaultLanguage = info.defaultLanguage;

    chatElement.defaultPrimaryText =
      info.defaultText ??
      `**Hey there, I'm Iniya! **
Got questions about Webomindapps? Ask me anything and I'll provide you with the info you're looking for.

** What I Can Help With..**
- I want to know about the UI/UX services
- Inform the sales team to reach out to me
- What are the digital marketing services offered?
- I want to visit the office
- Is the company experienced with Shopify?
- Share some recent projects
** Note **
There's much more I can answer — feel free to ask!`;

    chatElement.mainWindow = {
      back: () => {
        this.chat?.selectionWindow.classList.remove('hidden');
        this.chat?.chatWindow.classList.add('hidden');
        this.setChatWindowLayout('default');
        this.toggleFloatingButton(false);
      },
      close: () => {
        this.chat?.chatWindow.classList.add('hidden');
        this.setChatWindowLayout('default');
        this.toggleFloatingButton(false);
        rememberWidgetClosed();
      },
    };

    chatElement.addEventListener('witzo-widget-layout-change', (event: Event) => {
      const mode = (event as CustomEvent<{ mode?: 'default' | 'chat' | 'calendar' }>).detail?.mode;
      this.setChatWindowLayout(mode ?? 'default');
    });

    if (this.chat) {
      this.chat.chatElement = chatElement;
      this.chat.floatingBtn.style.backgroundColor = info.floatingBtn ?? '#fc0e3f';
      this.chat.chatIcon.style.fill = info.iconColor ?? '#fc0e3f';
      this.chat.voiceIcon.style.fill = info.iconColor ?? '#fc0e3f';
    }
  }

  set voiceInfo(info: {
    agentId: string;
    iconColor: Nullable<string>;
    apiUrl?: string;
    apiBaseUrl?: string;
    uniqueId?: string;
    bannerText?: Nullable<string>;
    bannerTextColor?: Nullable<string>;
    chatHeader?: Nullable<string>;
    logoIcon?: Nullable<string>;
    closeButtonColor?: Nullable<string>;
    bannerTextParagraph?: Nullable<string>;
    bannerTextParagraphColor?: Nullable<string>;
  }) {
    this._agentId = info.agentId;
    this.defineVoiceAgent(info);
  }

  private toggleFloatingButton(isHidden: boolean) {
    this.chat?.floatingButton.classList.remove('hidden');
    if (isHidden) {
      this.chat?.floatingButton.classList.add('hidden');
    }
  }

  private setChatWindowLayout(mode: 'default' | 'chat' | 'calendar') {
    if (!this.chat) return;

    this.chat.chatWindow.classList.toggle('chat-expanded', mode === 'chat');
    this.chat.chatWindow.classList.toggle('calendar-expanded', mode === 'calendar');
  }

  private hideSelectionWindow() {
    if (!this.chat) return;

    this.chat.selectionWindow.classList.add('hidden');
    this.chat.selectionWindow.style.display = '';
    this.chat.selectionWindow.style.opacity = '';
  }

  private defineVoiceAgent(info: {
    agentId: string;
    iconColor: Nullable<string>;
    apiUrl?: string;
    apiBaseUrl?: string;
    uniqueId?: string;
    bannerText?: Nullable<string>;
    bannerTextColor?: Nullable<string>;
    chatHeader?: Nullable<string>;
    logoIcon?: Nullable<string>;
    closeButtonColor?: Nullable<string>;
    bannerTextParagraph?: Nullable<string>;
    bannerTextParagraphColor?: Nullable<string>;
  }) {
    ensureDefined('witzo-voice-element', WitzoVoiceElement);

    const voiceElement = this.root.querySelector('witzo-voice-element') as WitzoVoiceElement;
    voiceElement.apiUrl = info.apiUrl ?? '';
    voiceElement.apiBaseURL = info.apiBaseUrl ?? '';
    voiceElement.uniqueId = info.uniqueId ?? '';
    voiceElement.iconColors = {
      iconColors: info.iconColor ?? '#fc0e3f',
    };

    // Apply the same design customization as chat
    if (info.bannerText) voiceElement.bannerText = info.bannerText;
    if (info.bannerTextColor) voiceElement.bannerTextColor = info.bannerTextColor;
    if (info.chatHeader) voiceElement.chatHeader = info.chatHeader;
    if (info.logoIcon) voiceElement.logoIcon = info.logoIcon;
    if (info.closeButtonColor) voiceElement.closeButtonColor = info.closeButtonColor;
    if (info.bannerTextParagraph) voiceElement.bannerTextParagraph = info.bannerTextParagraph;
    if (info.bannerTextParagraphColor) voiceElement.bannerTextParagraphColor = info.bannerTextParagraphColor;

    voiceElement.mainWindow = {
      back: () => {
        this.chat?.selectionWindow.classList.remove('hidden');
        this.chat?.chatWindow.classList.add('hidden');
        this.setChatWindowLayout('default');
        this.toggleFloatingButton(false);
      },
      close: () => {
        this.chat?.chatWindow.classList.add('hidden');
        this.setChatWindowLayout('default');
        this.toggleFloatingButton(false);
        rememberWidgetClosed();
      },
    };

    voiceElement.addEventListener('witzo-widget-layout-change', (event: Event) => {
      const mode = (event as CustomEvent<{ mode?: 'default' | 'chat' | 'calendar' }>).detail?.mode;
      this.setChatWindowLayout(mode ?? 'default');
    });

    // A live avatar session needs a taller window; grow/shrink on session start/stop.
    // setChatWindowLayout only toggles chat/calendar classes, so this is independent.
    voiceElement.addEventListener('witzo-widget-avatar-mode', (event: Event) => {
      const active = (event as CustomEvent<{ active?: boolean }>).detail?.active;
      this.chat?.chatWindow.classList.toggle('avatar-expanded', !!active);
    });

    if (this.chat) this.chat.voiceElement = voiceElement;
  }

  connectedCallback() {
    this.bindEvents();

    // Recover any voice session that wasn't cleanly ended (e.g. page refresh mid-call)
    recoverOrphanedSession().catch(() => {});

    this.scheduleAutoOpenSelection();
  }

  disconnectedCallback() {
    if (this.autoOpenTimer) {
      window.clearTimeout(this.autoOpenTimer);
      this.autoOpenTimer = null;
    }
  }

  bindEvents() {
    if (!this.chat || this.flags.isInitialized === false) return;
    const { chatSelectBtn, chatWindow, floatingButton, selectionWindow, voiceSelectBtn } =
      this.chat;

    floatingButton.addEventListener('click', () => {
      this.toggleButton();
    });

    chatSelectBtn.addEventListener('click', () => {
      this.openChat();
      this.toggleFloatingButton(true);
    });

    voiceSelectBtn.addEventListener('click', () => {
      this.openVoice();
      this.toggleFloatingButton(true);
    });
  }

  /**
   * @description toggle button for floating button
   */
  toggleButton() {
    if (!this.chat) return;
    this.flags.isButtonClicked = true;
    this.flags.isOpen = !this.flags.isOpen;
    this.flags.isOpen ? this.maximizeWidget() : this.minimizeWidget();
  }

  openSelection() {
    if (!this.chat) return;

    this.flags.isButtonClicked = true;
    this.flags.isOpen = true;
    this.chat.chatWindow.classList.add('hidden');
    this.chat.selectionWindow.classList.remove('hidden');
    this.chat.selectionWindow.style.display = 'block';
    this.chat.selectionWindow.style.opacity = '1';
    const selectionCard = this.chat.selectionWindow.querySelector<HTMLElement>('.selection-popup');
    if (selectionCard) {
      selectionCard.style.opacity = '1';
      selectionCard.style.transform = 'translateY(0)';
    }
    this.setChatWindowLayout('default');
    this.toggleFloatingButton(false);
  }

  openChat() {
    if (!this.chat) return;

    this.hideSelectionWindow();
    this.chat.chatWindow.classList.remove('hidden');
    this.setChatWindowLayout('default');
    this.chat.voiceElement?.classList.add('hidden');
    this.chat.chatElement?.classList.remove('hidden');
    (this.chat.chatElement as WitzoChatElement)?.maximizeWidget();
  }

  openVoice() {
    if (!this.chat) return;

    this.hideSelectionWindow();
    this.chat.chatWindow.classList.remove('hidden');
    this.setChatWindowLayout('default');
    this.chat.chatElement?.classList.add('hidden');
    this.chat.voiceElement?.classList.remove('hidden');
  }

  /**Minimises the chat window */
  private minimizeWidget() {
    if (!this.chat) return;

    this.hideSelectionWindow();
    this.setChatWindowLayout('default');
    rememberWidgetClosed();
  }

  /**Maximize the chat window */
  private maximizeWidget() {
    if (!this.chat) return;
    this.chat.selectionWindow.classList.remove('hidden');
    this.chat.selectionWindow.style.display = 'block';
    this.chat.selectionWindow.style.opacity = '1';
  }

  private scheduleAutoOpenSelection(attempt = 0) {
    if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);

    this.autoOpenTimer = window.setTimeout(() => {
      this.autoOpenTimer = null;
      if (this.flags.isButtonClicked || !this.isConnected) return;

      if (!this.chat || this.flags.isInitialized === false) {
        if (attempt < 8) this.scheduleAutoOpenSelection(attempt + 1);
        return;
      }

      this.openSelection();
    }, attempt === 0 ? 2000 : 250);
  }
}

export { WitzoVoiceChat };

